import { useState, useEffect } from 'react'
import { Modal } from '@/components/shared/Modal'
import { X, Archive, ArchiveRestore } from 'lucide-react'
import { TryOnGeneration, transformImage, buildTryOnSrcSet } from '@wtf/shared'
import {
    useArchiveTryOnGeneration,
    useUnarchiveTryOnGeneration,
} from '@/hooks/api/useTryOn'
import { useToast } from '@/components/shared/toast/Toast'
import { replaceImageDomain } from '@/utils/replaceImageDomain'

interface TryOnViewModalProps {
    open: boolean
    onClose: () => void
    generations: TryOnGeneration[]
    initialGenerationId: string
    onViewProduct: (productId: string) => void
}

export const TryOnViewModal = ({
    open,
    onClose,
    generations,
    initialGenerationId,
    onViewProduct,
}: TryOnViewModalProps) => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const archiveMutation = useArchiveTryOnGeneration()
    const unarchiveMutation = useUnarchiveTryOnGeneration()
    const { addToast } = useToast()

    // Update selected index when initialGenerationId changes
    useEffect(() => {
        const index = generations.findIndex(
            (gen) => gen.id === initialGenerationId
        )
        if (index !== -1) {
            setSelectedIndex(index)
        }
    }, [initialGenerationId, generations])

    // Keyboard navigation for desktop
    useEffect(() => {
        if (!open) return

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault()
                setSelectedIndex((prev) =>
                    prev === 0 ? generations.length - 1 : prev - 1
                )
            } else if (e.key === 'ArrowRight') {
                e.preventDefault()
                setSelectedIndex((prev) =>
                    prev === generations.length - 1 ? 0 : prev + 1
                )
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [open, generations.length])

    const currentGeneration = generations[selectedIndex]

    const handleArchive = () => {
        if (!currentGeneration) return

        if (currentGeneration.isArchived) {
            unarchiveMutation.mutate(currentGeneration.id, {
                onSuccess: () => {
                    addToast('Try-on restored successfully', 'success')
                    onClose()
                },
                onError: () => {
                    addToast('Failed to restore try-on', 'error')
                },
            })
        } else {
            archiveMutation.mutate(currentGeneration.id, {
                onSuccess: () => {
                    addToast('Try-on archived successfully', 'success')
                    onClose()
                },
                onError: () => {
                    addToast('Failed to archive try-on', 'error')
                },
            })
        }
    }

    const handleViewProduct = () => {
        if (currentGeneration?.productId) {
            onViewProduct(currentGeneration.productId)
            onClose()
        }
    }

    if (!currentGeneration) return null

    return (
        <Modal
            open={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) onClose()
            }}
            customWidth="w-[95vw] md:w-[90vw]"
            customMaxWidth="max-w-[1400px]"
            contentClassName="!p-0 m-0"
        >
            {/* Mobile Layout - Simple stacked */}
            <div className="relative w-full md:hidden">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-2 right-2 z-20 cursor-pointer rounded-full bg-white/90 p-2 transition-colors hover:bg-white"
                >
                    <X size={20} />
                </button>

                {/* Image - Natural sizing with minimal padding */}
                <div className="bg-white p-2 pt-12">
                    <div className="relative">
                        <picture>
                            <source
                                srcSet={replaceImageDomain(
                                    buildTryOnSrcSet(
                                        currentGeneration.generatedImageUrl,
                                        ['medium', 'large', 'xlarge']
                                    )
                                )}
                                sizes="(max-width: 768px) 100vw, 800px"
                                type="image/webp"
                            />
                            <img
                                src={replaceImageDomain(
                                    transformImage(
                                        currentGeneration.generatedImageUrl,
                                        'large'
                                    ) || currentGeneration.generatedImageUrl
                                )}
                                alt="Try-on result"
                                className="h-auto w-full rounded-lg"
                            />
                        </picture>

                        {/* Archive/Unarchive Button - Top Left on Image */}
                        <button
                            onClick={handleArchive}
                            aria-label={
                                currentGeneration.isArchived
                                    ? 'Restore try-on from archive'
                                    : 'Archive try-on'
                            }
                            title={
                                currentGeneration.isArchived
                                    ? 'Unarchive'
                                    : 'Archive'
                            }
                            className="absolute top-2 left-2 rounded-full bg-white/20 p-2 backdrop-blur-sm transition-colors hover:bg-white/30"
                        >
                            {currentGeneration.isArchived ? (
                                <ArchiveRestore
                                    size={16}
                                    className="text-white"
                                />
                            ) : (
                                <Archive size={16} className="text-white" />
                            )}
                        </button>

                        {/* View Product Button - Top Right on Image */}
                        {currentGeneration.productId && (
                            <button
                                onClick={handleViewProduct}
                                className="absolute top-2 right-2 rounded-lg bg-white/20 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/30"
                            >
                                View Product
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Desktop Layout - Flex with centering and thumbnails */}
            <div className="relative -my-4 hidden h-[85vh] w-full flex-col md:flex">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 cursor-pointer rounded-full bg-white/90 p-2 transition-colors hover:bg-white"
                >
                    <X size={24} />
                </button>

                {/* Main Image with Overlay Buttons */}
                <div className="relative flex min-h-0 flex-1 items-center justify-center bg-white p-6">
                    <div className="relative max-h-full max-w-full">
                        <picture>
                            <source
                                srcSet={replaceImageDomain(
                                    buildTryOnSrcSet(
                                        currentGeneration.generatedImageUrl,
                                        ['medium', 'large', 'xlarge']
                                    )
                                )}
                                sizes="(max-width: 768px) 100vw, 800px"
                                type="image/webp"
                            />
                            <img
                                src={replaceImageDomain(
                                    transformImage(
                                        currentGeneration.generatedImageUrl,
                                        'large'
                                    ) || currentGeneration.generatedImageUrl
                                )}
                                alt="Try-on result"
                                className="max-h-[calc(85vh-160px)] w-auto rounded-lg object-contain"
                            />
                        </picture>

                        {/* Archive/Unarchive Button - Top Left on Image */}
                        <button
                            onClick={handleArchive}
                            aria-label={
                                currentGeneration.isArchived
                                    ? 'Restore try-on from archive'
                                    : 'Archive try-on'
                            }
                            title={
                                currentGeneration.isArchived
                                    ? 'Unarchive'
                                    : 'Archive'
                            }
                            className="absolute top-2 left-2 rounded-full bg-white/20 p-3 backdrop-blur-sm transition-colors hover:bg-white/30"
                        >
                            {currentGeneration.isArchived ? (
                                <ArchiveRestore
                                    size={20}
                                    className="text-white"
                                />
                            ) : (
                                <Archive size={20} className="text-white" />
                            )}
                        </button>

                        {/* View Product Button - Top Right on Image */}
                        {currentGeneration.productId && (
                            <button
                                onClick={handleViewProduct}
                                className="absolute top-2 right-2 rounded-lg bg-white/20 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/30"
                            >
                                View Product
                            </button>
                        )}
                    </div>
                </div>

                {/* Thumbnail Strip - Desktop Only */}
                {generations.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto border-t border-gray-200 bg-gray-50 p-4">
                        {generations.map((generation, index) => (
                            <button
                                key={generation.id}
                                onClick={() => setSelectedIndex(index)}
                                className={`relative shrink-0 overflow-hidden rounded-lg transition-all ${
                                    selectedIndex === index
                                        ? 'ring-2 ring-black ring-offset-2'
                                        : 'opacity-60 hover:opacity-100'
                                }`}
                            >
                                <img
                                    src={replaceImageDomain(
                                        transformImage(
                                            generation.generatedImageUrl,
                                            'small'
                                        ) || generation.generatedImageUrl
                                    )}
                                    alt={`Try-on ${index + 1}`}
                                    className="h-24 w-24 object-cover"
                                />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    )
}
